import React from 'react';
import { Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import { darkenColor, adjustOpacity } from '../../../theme';

/**
 * MetricCard Component
 *
 * A reusable card component for displaying key metrics with icon, value, subtitle, and title.
 * Enhanced with modern gradient backgrounds similar to ExperimentResults.
 *
 * @param {string} title - The main title/label for the metric
 * @param {string|number} value - The primary metric value to display
 * @param {string} subtitle - Secondary information below the value
 * @param {React.ReactNode} icon - Icon component to display
 * @param {string} color - MUI color theme (primary, success, warning, error, info, secondary)
 * @param {object} sx - Additional styling overrides
 */
const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  sx = {},
}) => {
  const theme = useTheme();

  // Get theme colors for the specified color variant
  const getColorGradient = colorName => {
    const paletteColor = theme.palette[colorName] || theme.palette.primary;
    const mainColor = paletteColor.main;
    const darkColor = paletteColor.dark || darkenColor(mainColor, 20);

    return {
      background: `linear-gradient(135deg, ${mainColor} 0%, ${darkColor} 100%)`,
      shadow: adjustOpacity(mainColor, 0.2),
      shadowHover: adjustOpacity(mainColor, 0.3),
    };
  };

  // Color gradient mapping using theme colors
  const colorGradients = {
    primary: getColorGradient('primary'),
    success: getColorGradient('success'),
    warning: getColorGradient('warning'),
    error: getColorGradient('error'),
    info: getColorGradient('info'),
    secondary: getColorGradient('secondary'),
  };

  const gradient = colorGradients[color] || colorGradients.primary;
  const contrastText =
    theme.palette[color]?.contrastText || theme.palette.common.white;

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 140,
        background: gradient.background,
        color: contrastText,
        borderRadius: 2,
        boxShadow: `0 4px 12px ${gradient.shadow}`,
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 20px ${gradient.shadowHover}`,
        },
        ...sx,
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 1.5,
          flex: 1,
        }}
      >
        {/* Icon - Centered */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.75 }}>
          {React.cloneElement(icon, {
            sx: {
              fontSize: '1.125rem', // Reduced from 1.25rem
              color: contrastText,
              ...icon.props?.sx,
            },
          })}
        </Box>

        {/* Title */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.75 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: contrastText,
              fontSize: '0.875rem', // Reduced from 0.9375rem
              textAlign: 'center',
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Value */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: contrastText,
              lineHeight: 1.2,
              fontSize: '1.5rem', // Reduced from 1.75rem
              mb: subtitle ? 0.25 : 0,
              textAlign: 'center',
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: adjustOpacity(contrastText, 0.9),
                fontSize: '0.75rem', // Reduced from 0.8125rem
                lineHeight: 1.2,
                textAlign: 'center',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricCard;
